<template>
  <section
    id="pipeline"
    class="pipeline module white">
    <div class="container large">
      <div class="pipeline-table">
        <div class="pipeline-head">
          <div class="program-head">
            <p>Programs</p>
          </div>
          <div class="stages-head">
            <p>Preclinical</p>
            <p>IND-Enabling</p>
            <p>Phase 1</p>
            <p>Phase 2</p>
            <p>Phase 3</p>
            <p>NDA</p>
          </div>
        </div>
        <div class="pipeline-body border-radius">
          <div class="pipeline-graph">
            <div class="line"></div>
            <div class="line"></div>
            <div class="line"></div>
            <div class="line"></div>
            <div class="line"></div>
            <div class="line"></div>
          </div>
          <div
            v-for="(section, index) in data.pipeline_section"
            :key="index"
            class="pipeline-section">
            <div
              class="section-title"
              :class="section.pipeline_category.value">
              <p>{{ section.pipeline_category.label }}</p>
              <div class="section-line"></div>
            </div>
            <PipelineItem
              v-for="(item, index) in section.pipeline"
              :key="index"
              :item="item"
              :type="section.pipeline_category.value" />
          </div>
        </div>
      </div>
      <div
        v-if="data.pipeline_legend.length"
        class="pipeline-legend desktop">
        <div class="pipeline-legend-wrapper">
          <div class="legend-item oncology">
            <p>Oncology</p>
          </div>
          <div class="legend-item immunology">
            <p>Immunology</p>
          </div>
          <div class="legend-item neuroscience">
            <p>Neuroscience</p>
          </div>
        </div>
      </div>
      <div
        v-if="data.pipeline_disclaimer_title || data.pipeline_disclaimer_text"
        class="pipeline-disclaimer">
        <div
          class="desc-title"
          v-html="data.pipeline_disclaimer_title"></div>
        <div
          class="desc-text"
          v-html="data.pipeline_disclaimer_text"></div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "PipelineComponent",
    "CLASSNAME": "pipeline",
    "URL": "http://eikon.local/"
   }
  #ENDMETA */
import PipelineItem from '@/components/modules/Pipeline/PipelineItem.vue'

defineProps(['data'])
</script>

<style lang="scss">
.pipeline {
  .container {
    @include tablet-landscape(1) {
      width: 95%;
    }

    .pipeline-table {
      padding: 75px 0 0;

      .pipeline-head {
        display: flex;
        align-items: center;
        margin: 0 0 16px;

        .program-head {
          width: 26.5%;

          p {
            padding-left: 30px;
            letter-spacing: 1.44px;
            text-transform: uppercase;
            @include font-main-med;

            @include tablet-landscape(1) {
              padding-left: 15px;
            }
          }
        }

        .stages-head {
          display: flex;
          justify-content: space-between;
          flex: 1;

          @include tablet-landscape(1) {
            display: none;
          }

          p {
            position: relative;
            top: -30px;
            flex: 1;
            white-space: nowrap;
            transform: translate(18%, -92%) rotate(-60deg);
            @include font-main-bold;
          }
        }
      }

      .pipeline-body {
        position: relative;
        padding: 30px 0;
        border: 1px solid $light-gray;

        @include mobile {
          display: flex;
          flex-direction: column;
        }

        .pipeline-graph {
          display: flex;
          position: absolute;
          top: 0;
          right: 0;
          width: calc(100% - 26.5%);
          height: 100%;

          @include tablet-landscape(1) {
            display: none;
          }

          .line {
            flex: 1;
            height: 100%;
            border-left: 1px solid $light-gray;
          }
        }

        .pipeline-section {
          &:last-child {
            .pipeline-item:last-child {
              border-bottom: none;
            }
          }

          .section-title {
            display: flex;
            align-items: center;

            &.oncology {
              p {
                color: $indigo;
              }

              .section-line {
                background: linear-gradient(
                  277deg,
                  #5340e2 -0.1%,
                  #64bdff 85.65%
                );
              }
            }

            &.immunology {
              p {
                color: $orange;
              }

              .section-line {
                background: linear-gradient(
                  84deg,
                  #fbff3e -4.42%,
                  #fc9024 80.16%
                );
              }
            }

            &.neuroscience {
              p {
                color: $magenta;
              }

              .section-line {
                background: linear-gradient(90deg, #ef3a62 0%, #872b6d 100%);
              }
            }

            p {
              line-height: 1;
              letter-spacing: 1.6px;
              text-transform: uppercase;
              margin: 20px 0 20px 28px;
              @include font-main-xtrabold;

              @include tablet-landscape(1) {
                margin-left: 10px;
              }
            }

            .section-line {
              flex: 1;
              height: 2px;
              margin-left: 24px;
              margin-right: 32px;

              @include tablet-landscape(1) {
                margin-right: 10px;
              }
            }
          }
        }
      }
    }

    .pipeline-legend {
      display: flex;

      .pipeline-legend-wrapper {
        display: flex;
        align-items: center;
        padding: 27px 24px;
        border-radius: 40px;
        background-color: $light-gray;

        @include mobile {
          padding: 20px 10px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          font-size: 1.8rem;
          margin-right: 60px;

          @include mobile {
            font-size: 1.6rem;
            margin-right: 6px;
          }

          &:last-child {
            margin: 0;
          }

          &.oncology:before {
            background: linear-gradient(
              91.54deg,
              #64bdff 28.46%,
              #5340e2 92.75%
            );
          }

          &.immunology:before {
            background: linear-gradient(
              84.34deg,
              #fbff3e -4.42%,
              #fc9024 80.16%
            );
          }

          &.neuroscience:before {
            background: linear-gradient(90deg, #ef3a62 0%, #872b6d 100%);
          }

          &:before {
            content: '';
            display: block;
            width: 16px;
            height: 16px;
            margin-right: 24px;
            border-radius: 50%;

            @include mobile {
              margin-right: 8px;
            }
          }
        }
      }

      &.mobile {
        margin-bottom: 20px;
        @media (min-width: 1024px) {
          display: none;
        }
      }

      &.desktop {
        margin: 40px 0 0;
      }
    }

    .pipeline-disclaimer {
      margin: 80px 0 0;

      @include tablet-landscape {
        margin: 100px 0 0;
      }

      @include mobile {
        margin: 60px 0 0;
      }

      .desc-title {
        font-size: 2rem;
        letter-spacing: 1.6px;
        text-transform: uppercase;
        color: $orange;
        @include font-main-xtrabold;
      }

      .desc-text {
        margin: 24px 0 0;
      }
    }
  }
}

.pipeline + .pipeline {
  padding-top: 0;
}
</style>
